import { SxProps, Theme } from "@mui/material";
import Flex from "./Flex";
import Text from "./Text";
import { CHART_COLORS, CHART_TYPOGRAPHY } from "./app/provider/patient/chart";

export type ActivityTimelineItem = {
  id: string;
  title: string;
  /** Secondary line, e.g. "A. Admin · Jun 1 · 9:15 AM". */
  meta?: string;
};

export type ActivityTimelineProps = {
  items: ActivityTimelineItem[];
  emptyText?: string;
  sx?: SxProps<Theme>;
};

const DOT_SIZE = 6;

/**
 * Vertical list of dated entries — audit trails, change history, activity feeds.
 *
 * @author Rajkumar Rathod
 */
const ActivityTimeline = ({ items, emptyText, sx }: ActivityTimelineProps) => {
  if (!items.length) {
    return (
      <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
        {emptyText}
      </Text>
    );
  }

  return (
    <Flex
      component="ul"
      flexDirection="column"
      gap={1.5}
      sx={{ m: 0, p: 0, listStyle: "none", ...(sx as object) }}
    >
      {items.map((item) => (
        <Flex component="li" key={item.id} gap={1} alignItems="flex-start">
          <Flex
            sx={{
              width: DOT_SIZE,
              height: DOT_SIZE,
              borderRadius: "50%",
              bgcolor: CHART_COLORS.primaryMid,
              flexShrink: 0,
              mt: "5px",
            }}
          />

          <Flex flexDirection="column" gap={0.25} minWidth={0}>
            <Text
              {...CHART_TYPOGRAPHY.body5}
              fontWeight={CHART_TYPOGRAPHY.weight.medium}
              c={CHART_COLORS.textDarker}
            >
              {item.title}
            </Text>
            {!!item.meta && (
              <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
                {item.meta}
              </Text>
            )}
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};

export default ActivityTimeline;
